import { ApiError, type ApiClient } from "@ncp/api-client";
import { transferables, type HostOp, type HostToPage, type PageToHost } from "@ncp/page-sdk/protocol";

/** What POST /api/pages/{key}/session answers. */
export interface PageSessionInfo {
  runUrl: string;
  pageToken: string;
  expiresAt: string;
  page: { id: string; key: string; version: number; name: string; nameAr?: string | null; icon: string; status: string; spaces: string[]; slots: string[] };
  space: string;
  slot: string;
  matterId: string | null;
  draft: boolean;
  entries: string[];
  agents: Array<{ key: string; name: string; nameAr?: string | null; greeting?: string | null; greetingAr?: string | null; suggestions: Array<{ en: string; ar?: string | null }>; tier: string }>;
  clientTools: Array<{ name: string; description: string; parameters?: Record<string, unknown> | null }>;
  capabilities: string[];
  grants: string[];
  buildError: string | null;
  /** The page-kit theme: the space's, unless the request named one. */
  theme?: string;
  // ---- ECONIC additions (shell-ia §15.5); older APIs leave them out.
  /** F1: the bundle served is older than its libraries; a rebuild is queued and `page.rebuilt` announces the new version. */
  stale?: boolean;
  /** The vendor set the page runs on (F2 warm-up). */
  vendorSet?: string;
  /** F4: read-only calls the page declares to run while it loads (page.json `prefetch`). */
  prefetch?: Array<{ capability: string; input?: unknown }>;
  /** The desk's classification floor from its space manifest, when the API knows it. */
  classification?: string | null;
  /** The appearances the page draws correctly (page.json `schemes`); absent: light only. */
  schemes?: string[] | null;
}

/** What the host does for a page: the parts of the bridge that need the workspace's UI. */
export interface HostActions {
  /** Whether the page is in view; a kept frame that is hidden may still compute and fetch, not interrupt the person. */
  visible(): boolean;
  ready(): void;
  toast(args: { message: string; tone?: string; description?: string }): void;
  confirm(args: { title: string; body?: string; confirmLabel?: string; cancelLabel?: string; tone?: string }): Promise<boolean>;
  open(target: Record<string, unknown>): void;
  title(title: string): void;
  resize(height: number): void;
  state(state: Record<string, unknown>): void;
  tools(names: string[]): void;
  mode(mode: string): void;
  route(route: { path?: string; params?: Record<string, string>; replace?: boolean }): void;
  record(args: { maxSeconds?: number; prompt?: string }): Promise<{ data: ArrayBuffer; type: string; durationMs: number }>;
  /** A heartbeat from inside the frame (the person is working there). */
  activity(): void;
  /** The page raised the host banner to a level (or dropped its raise with `level: null`). */
  classification(args: { level?: unknown; source?: unknown }): void;
  /** A capability call the page made (the host learns a page's first-view reads to prefetch them next time, F4). */
  called?(capability: string, input: unknown): void;
}

const MAX_FILE_BYTES = 50 * 1024 * 1024;

function pickFiles(args: { accept?: string; multiple?: boolean }): Promise<Array<{ name: string; type: string; data: ArrayBuffer }>> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    if (args.accept) input.accept = args.accept;
    input.multiple = !!args.multiple;
    input.style.display = "none";
    document.body.appendChild(input);
    const done = () => input.remove();
    input.addEventListener("cancel", () => { done(); resolve([]); });
    input.addEventListener("change", async () => {
      try {
        const files = Array.from(input.files ?? []);
        const big = files.find((f) => f.size > MAX_FILE_BYTES);
        if (big) throw new Error(`${big.name} is larger than 50 MB.`);
        resolve(await Promise.all(files.map(async (f) => ({ name: f.name, type: f.type, data: await f.arrayBuffer() }))));
      } catch (e) {
        reject(e);
      } finally {
        done();
      }
    });
    input.click();
  });
}

function download(args: { name: string; type: string; data: ArrayBuffer }) {
  const url = URL.createObjectURL(new Blob([args.data], { type: args.type || "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = args.name.replace(/[\\/:*?"<>|]+/g, "_").slice(0, 160) || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const isSessionError = (e: unknown) => e instanceof ApiError && e.problem?.code === "session";

/** What a page may only do while the person is looking at it: move the workspace, take over the screen, or ask for something. */
const IN_VIEW_ONLY = new Set<HostOp>(["confirm", "open", "navigate", "route", "title", "mode", "files.pick", "media.record"]);

/**
 * The host's end of a page's bridge. The page posts calls on its MessagePort; the host adds the person's sign-in (the API client) and the
 * page token, calls the capability registry and posts the answer back. Page agents' client tools come back the other way: a client_call
 * in the chat stream becomes a tool message to the page, and its answer is posted to the waiting agent.
 */
export class HostBridge {
  private port: MessagePort | null = null;
  private readonly running = new Map<number, AbortController>();
  private renewing: Promise<unknown> | null = null;
  /** Client tools the host itself asked the page to run (a button in a copilot answer's block); their answers stay here. */
  private readonly localTools = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private localSeq = 0;

  constructor(
    private readonly api: ApiClient,
    private readonly session: () => PageSessionInfo,
    private readonly renew: () => Promise<unknown>,
    private readonly actions: HostActions,
    private readonly locale: () => string,
  ) {}

  attach(port: MessagePort): void {
    this.detach();
    this.port = port;
    port.onmessage = (e: MessageEvent<PageToHost>) => void this.receive(e.data);
    port.start();
  }

  detach(): void {
    for (const c of this.running.values()) c.abort();
    this.running.clear();
    for (const p of this.localTools.values()) p.reject(new Error("The page closed."));
    this.localTools.clear();
    this.port?.close();
    this.port = null;
  }

  /** A capability call in the page's session, for page-kit drawn in the host (the copilot's answer blocks). */
  invoke(capability: string, input: unknown, signal?: AbortSignal): Promise<unknown> {
    return this.withSession(async () => (await this.api.post<{ result: unknown }>("/api/pages/rpc", { capability, input: input ?? {}, locale: this.locale() }, { headers: this.headers(), signal })).result);
  }

  /** The current page token, renewed first when it is about to expire (the copilot sends it with each message). */
  async freshToken(): Promise<string> {
    const s = this.session();
    if (Date.parse(s.expiresAt) - Date.now() < 60_000) {
      this.renewing ??= this.renew().finally(() => { this.renewing = null; });
      await this.renewing;
    }
    return this.session().pageToken;
  }

  /** The copilot's agent called one of the page's client tools: the page runs it and its answer goes to the waiting turn. */
  runTool(call: { callId: string; tool: string; arguments: Record<string, unknown> }): void {
    if (!this.port) {
      void this.api.post("/api/pages/rpc/client-result", { callId: call.callId, result: null, error: "The page is not open." }).catch(() => undefined);
      return;
    }
    this.post({ kind: "tool", callId: call.callId, tool: call.tool, arguments: call.arguments ?? {} });
  }

  /** Runs one of the page's client tools for the host itself and returns the page's answer. */
  invokeTool(name: string, args: unknown): Promise<unknown> {
    if (!this.port) return Promise.reject(new Error("The page is not open."));
    const callId = `host-${++this.localSeq}`;
    return new Promise((resolve, reject) => {
      this.localTools.set(callId, { resolve, reject });
      this.post({ kind: "tool", callId, tool: name, arguments: (args ?? {}) as Record<string, unknown> });
    });
  }

  get connected(): boolean {
    return this.port !== null;
  }

  /** Tells the page something changed on the host side (locale, matter, route, platform events). */
  emit(name: string, payload: unknown): void {
    this.post({ kind: "event", name: name as never, payload });
  }

  private post(message: HostToPage): void {
    try {
      this.port?.postMessage(message, transferables(message));
    } catch {
      // the frame is gone
    }
  }

  private headers(): Record<string, string> {
    return { "X-Ncp-Page-Token": this.session().pageToken };
  }

  /** Runs a bridged request; when the page session expired, renews it once and tries again. */
  private async withSession<T>(run: () => Promise<T>): Promise<T> {
    try {
      return await run();
    } catch (e) {
      if (!isSessionError(e)) throw e;
      this.renewing ??= this.renew().finally(() => { this.renewing = null; });
      await this.renewing;
      return run();
    }
  }

  private fail(id: number, e: unknown): void {
    if (e instanceof DOMException && e.name === "AbortError") return;
    if (e instanceof ApiError) this.post({ kind: "error", id, error: e.problem?.error ?? e.message, code: e.problem?.code ?? undefined, status: e.status });
    else this.post({ kind: "error", id, error: e instanceof Error ? e.message : String(e), code: "error" });
  }

  private track(id: number): AbortController {
    const ac = new AbortController();
    this.running.set(id, ac);
    return ac;
  }

  private async receive(m: PageToHost): Promise<void> {
    switch (m.kind) {
      case "ready":
        this.actions.ready();
        return;
      case "cancel":
        this.running.get(m.id)?.abort();
        this.running.delete(m.id);
        return;
      case "tool-result": {
        const local = this.localTools.get(m.callId);
        if (local) {
          this.localTools.delete(m.callId);
          if (m.error) local.reject(new Error(m.error));
          else local.resolve(m.result);
          return;
        }
        await this.api.post("/api/pages/rpc/client-result", { callId: m.callId, result: m.result ?? null, error: m.error ?? null }).catch(() => undefined);
        return;
      }
      case "call": {
        this.actions.called?.(m.capability, m.input);
        const ac = this.track(m.id);
        try {
          const res = await this.withSession(() => this.api.post<{ result: unknown }>("/api/pages/rpc", { capability: m.capability, input: m.input ?? {}, locale: this.locale() }, { headers: this.headers(), signal: ac.signal }));
          this.post({ kind: "result", id: m.id, result: res.result });
        } catch (e) {
          this.fail(m.id, e);
        } finally {
          this.running.delete(m.id);
        }
        return;
      }
      case "arrow": {
        const ac = this.track(m.id);
        try {
          const res = await this.withSession(() => this.api.postBytes("/api/pages/rpc/arrow", { capability: m.capability, input: m.input ?? {}, locale: this.locale() }, ac.signal, this.headers()));
          this.post({ kind: "result", id: m.id, result: res.data });
        } catch (e) {
          this.fail(m.id, e);
        } finally {
          this.running.delete(m.id);
        }
        return;
      }
      case "stream": {
        const ac = this.track(m.id);
        try {
          await this.withSession(async () => {
            for await (const frame of this.api.streamEvents<Record<string, unknown>>("/api/pages/rpc/stream", { capability: m.capability, input: m.input ?? {}, locale: this.locale() }, ac.signal, this.headers())) {
              if (frame.type === "done") break;
              if (frame.type === "error") throw new ApiError(500, String(frame.error ?? "The stream failed."), { error: String(frame.error ?? ""), code: String(frame.code ?? "error") });
              this.post({ kind: "chunk", id: m.id, chunk: frame });
            }
          });
          this.post({ kind: "end", id: m.id });
        } catch (e) {
          this.fail(m.id, e);
        } finally {
          this.running.delete(m.id);
        }
        return;
      }
      case "chat": {
        const ac = this.track(m.id);
        try {
          await this.withSession(async () => {
            const body = { agent: m.agent, message: m.message, threadId: m.threadId ?? null, state: m.state ?? null, locale: this.locale() };
            for await (const event of this.api.streamEvents<Record<string, unknown>>("/api/pages/chat", body, ac.signal, this.headers())) {
              if (event.type === "client_call") this.post({ kind: "tool", callId: String(event.callId), tool: String(event.tool), arguments: event.arguments ?? {} });
              this.post({ kind: "chunk", id: m.id, chunk: event });
            }
          });
          this.post({ kind: "end", id: m.id });
        } catch (e) {
          this.fail(m.id, e);
        } finally {
          this.running.delete(m.id);
        }
        return;
      }
      case "host": {
        try {
          this.post({ kind: "result", id: m.id, result: await this.hostOp(m.op, (m.args ?? {}) as Record<string, unknown>) });
        } catch (e) {
          this.fail(m.id, e);
        }
        return;
      }
    }
  }

  private async hostOp(op: HostOp, args: Record<string, unknown>): Promise<unknown> {
    if (IN_VIEW_ONLY.has(op) && !this.actions.visible()) throw new Error("The page is not in view.");
    switch (op) {
      case "toast":
        this.actions.toast(args as { message: string; tone?: string; description?: string });
        return null;
      case "confirm":
        return this.actions.confirm(args as { title: string });
      case "open":
      case "navigate":
        this.actions.open(args);
        return null;
      case "title":
        this.actions.title(String(args.title ?? ""));
        return null;
      case "resize":
        this.actions.resize(Math.max(0, Math.min(20_000, Number(args.height) || 0)));
        return null;
      case "state":
        this.actions.state(args);
        return null;
      case "tools":
        this.actions.tools(Array.isArray(args.names) ? args.names.map(String) : []);
        return null;
      case "mode":
        this.actions.mode(String(args.mode ?? "normal"));
        return null;
      case "route":
        this.actions.route(args as { path?: string; params?: Record<string, string>; replace?: boolean });
        return null;
      case "download":
        download(args as { name: string; type: string; data: ArrayBuffer });
        return null;
      case "files.pick":
        return pickFiles(args as { accept?: string; multiple?: boolean });
      case "media.record":
        return this.actions.record(args as { maxSeconds?: number; prompt?: string });
      case "threads":
        return this.withSession(() => this.api.get("/api/pages/chat/threads", { agent: String(args.agent ?? "") }, { headers: this.headers() }));
      case "messages":
        return this.withSession(() => this.api.get(`/api/pages/chat/threads/${encodeURIComponent(String(args.threadId ?? ""))}`, undefined, { headers: this.headers() }));
      case "print":
        return null;
      case "activity":
        this.actions.activity();
        return null;
      case "classification":
        this.actions.classification(args as { level?: unknown; source?: unknown });
        return null;
    }
  }
}
